import React, { useReducer, useRef, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import {
    UserInputs,
    LocationsDropdownWrapper,
    consultSchema,
    reducer,
    NearbyLocations,
    ShowAddressOrLink,
    initialFormData,
    initialUserData,
    FORM_ACTIONS,
    FORM_PROGRESS,
} from '.';
import { NETLIFY_FUNCTIONS_PATH, getFacebookCookies, validateWithPossibleNow } from '../../../utils';
import axios from 'axios';
import { FormSuccess, FormFailed, FormSubmitting, AttributionSurvey, ConsentMsg } from '../shared';
import { useScrollToAnchor } from '../../../hooks/useScrollToAnchor.ts';

export function NotOpen({ siteData }) {
    if (siteData.currentMetro.stores.length === 1) {
        initialFormData.store = siteData.currentMetro.stores[0];
    }
    const [formState, dispatch] = useReducer(reducer, initialFormData);

    const isBrowser = typeof window !== 'undefined';
    const cloudampDataRef = useRef(null);

    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    // State to hold form values for use in attribution survey
    const [formValues, setFormValues] = useState(null);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });
    const apiEndpoint = `${NETLIFY_FUNCTIONS_PATH}consult-form-handler`;

    //used to capture the cloudamp data value and store it in localStorage
    useEffect(() => {
        // Wait for 3 seconds before accessing the value
        const timer = setTimeout(() => {
            if (cloudampDataRef.current) {
                // Set the value of localStorage to the value cloudampDataRef.current.value
                localStorage.setItem('cloudamp__data__c', cloudampDataRef.current.value);
            }
        }, 5000);
        // Cleanup timer on component unmount
        return () => clearTimeout(timer);
    }, []);

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('consult-form');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        try {
            // Validate phone and email with PossibleNow
            const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                values.phone,
                values.email
            );

            const formData = {
                ...values,
                city: formState.metro.name,
                state: formState.metro.stateAbbrev,
                location: formState.store.salesforceValue,
                consultType: formState.include.formAction,
                updates: true,
                cloudamp__data__c: cloudampDataRef.current.value,
                fbp,
                fbc,
                phoneFound,
                wireless,
                emailFound,
                result,
                flags,
            };

            // Store form values for use in attribution survey - only for Ask Question action
            setFormValues({ formType: 'question', ...formData });

            // Submit to Netlify function
            const response = await axios.post(apiEndpoint, formData);

            helpers.setSubmitting(false);
            helpers.setStatus('Success');

            // If backend returned a Self-Schedule URL, perform a top-level redirect
            if (isBrowser && formState.include.formAction === FORM_ACTIONS.selfSchedule && response.data.redirectUrl) {
                helpers.resetForm();
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                });
                window.location.href = response.data.redirectUrl;
                return;
            } else {
                setShowAttributionSurvey(true);
                helpers.resetForm();
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                });
            }
        } catch (error) {
            console.error('Form submission error:', error);
            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setFormValues(null);

            setTimeout(() => {
                helpers.resetForm();
                dispatch({
                    type: 'formAction',
                    formAction: '',
                    formProgress: '',
                    user: initialUserData,
                });
                setShowAttributionSurvey(false);
            }, 5000);
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);
        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <div id="consult-form" className="hr-divider hr-divider-consult">
            <h2 className="text-center subheadsm">Get Notified Once We Open</h2>
            <Formik
                initialValues={initialUserData}
                validationSchema={consultSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {
                    // This way we can get access to all formik props
                    (formik) => {
                        return (
                            <Form className="container" method="POST">
                                <input ref={cloudampDataRef} type="hidden" name="cloudamp__data__c" />

                                {formik.isSubmitting && <FormSubmitting />}
                                {formik.status === 'Failed' && <FormFailed />}
                                {/* Only for Ask Question Action */}
                                {formValues && !formik.isSubmitting && formik.status !== 'Failed' && (
                                    <>
                                        {showAttributionSurvey ? (
                                            <div className="row justify-content-center">
                                                <div className="col-12 col-xl-10">
                                                    <AttributionSurvey
                                                        formValues={formValues}
                                                        onComplete={handleAttributionComplete}
                                                        isFullWidth={false}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <FormSuccess />
                                        )}
                                    </>
                                )}

                                {/* Original form content - only shown when not in special states */}
                                {!formik.isSubmitting && formik.status !== 'Failed' && !formValues && (
                                    <>
                                        <UserInputs isQuestion formik={formik} />
                                        {siteData.currentMetro.stores.length > 1 && (
                                            <div className="locations-container">
                                                <div
                                                    className={`locations-wrapper mx-2 ${
                                                        formState.include.formProgress === FORM_PROGRESS.selectLocation
                                                            ? 'toggle'
                                                            : ''
                                                    }`}
                                                >
                                                    <NearbyLocations
                                                        currentMetro={siteData.currentMetro}
                                                        dispatch={dispatch}
                                                    />
                                                    <LocationsDropdownWrapper
                                                        {...{
                                                            formState,
                                                            dispatch,
                                                            siteData,
                                                        }}
                                                    />

                                                    <div className="row justify-content-center mx-auto selected-location-container">
                                                        <div
                                                            className={`col-md-10 selected-location-wrapper ${
                                                                formState.store.salesforceValue &&
                                                                formState.store.salesforceValue !== ''
                                                                    ? 'toggle'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <div className="mb-0 text-center">
                                                                <ShowAddressOrLink
                                                                    formState={formState}
                                                                    currentMetro={siteData.currentMetro}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {formState.include.formAction === '' && (
                                            <div className="row justify-content-center mb-4">
                                                <div className="col-xl-10 lh-1">
                                                    <ConsentMsg
                                                        cta={
                                                            'Ask A Question<sup>*</sup> or See Available Times<sup>**</sup>'
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div id="actions-btns" className="row gx-1 gy-4 justify-content-center">
                                            <div className="col-sm-5 col-lg-4 col-xl-3 text-center">
                                                {siteData.currentMetro.stores.length > 1 &&
                                                    formState.include.formProgress !== FORM_PROGRESS.selectLocation && (
                                                        <button
                                                            className="milan-btn milan-navy-btn w-100"
                                                            onClick={() =>
                                                                dispatch({
                                                                    type: 'formAction',
                                                                    formAction: FORM_ACTIONS.question,
                                                                    formProgress: FORM_PROGRESS.selectLocation,
                                                                    user: formik.values,
                                                                })
                                                            }
                                                        >
                                                            SELECT A LOCATION
                                                        </button>
                                                    )}
                                                {(siteData.currentMetro.stores.length === 1 ||
                                                    formState.include.formProgress ===
                                                        FORM_PROGRESS.selectLocation) && (
                                                    <button
                                                        type="submit"
                                                        className="milan-btn milan-red-btn w-100 mt-4"
                                                        onClick={() =>
                                                            dispatch({
                                                                type: 'formAction',
                                                                formAction: FORM_ACTIONS.question,
                                                                formProgress: FORM_PROGRESS.selectLocation, // if this set to '' it won't submit
                                                                user: formik.values,
                                                            })
                                                        }
                                                        disabled={!formik.isValid ? true : false}
                                                    >
                                                        {formik.isSubmitting ? 'Submitting...' : 'Submit'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Form>
                        );
                    }
                }
            </Formik>
        </div>
    );
}

export default NotOpen;
